import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { get, post } from "src/utils/networkUtils";
import { DrawList, DrawResultCard, DrawRunPanel } from "../components/winners";

const PREVIEW_URL = "/admin/gameweek-prize-draw/preview";
const RUN_URL = "/admin/gameweek-prize-draw/run";

export default function EcffWinnersPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(() => {
    setLoading(true);
    setLoadError("");
    // get() rejects on non-2xx; the guard message (e.g. "Gameweek 1 deadline has not yet
    // passed") lives at err.response.data.message. Keep the inline Alert as the page-local
    // surface even though the global interceptor also pops a toast for non-401 errors.
    get(PREVIEW_URL)
      .then((r) => {
        const data = r?.data ?? null;
        setPreview(data);
        // If a draw already exists, surface the recorded result immediately (idempotent view).
        if (data?.existing_draw) {
          setResult(data.existing_draw);
        }
      })
      .catch((err) => {
        setPreview(null);
        setLoadError(
          err?.response?.data?.message ??
            err?.message ??
            "Failed to load the prize-draw preview."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleConfirmRun = async () => {
    setRunError("");
    setRunning(true);
    try {
      const r = await post(RUN_URL);
      const data = r?.data;
      // Fail closed (INT-3): a mis-allowlisted / stubbed POST resolves a fabricated success with
      // no winner or seed. Treat the run as successful ONLY when both are present.
      if (!data?.winner_user_id || !data?.seed) {
        setRunError("Draw did not run - check API wiring.");
        return;
      }
      setResult(data);
      setConfirmOpen(false);
      // refetch so already_drawn / eligibility reflect the recorded draw
      loadPreview();
    } catch (err) {
      setRunError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to run the prize draw."
      );
    } finally {
      setRunning(false);
    }
  };

  // Only the Gameweek-1 prize draw exists today; this is deliberately an array so more draw
  // types can be appended (each with its own fetch) without reshaping the page.
  const recorded = Boolean(result) || Boolean(preview?.already_drawn);
  const draws = preview
    ? [
        {
          id: `${preview.tournament_calendar_id}:gw${preview.gameweek_number}`,
          competitionName: preview.competition_name,
          seasonLabel: preview.season_label,
          periodLabel: `Gameweek ${preview.gameweek_number}`,
          status: recorded ? "recorded" : "pending",
          preview,
          result,
        },
      ]
    : [];

  const selectedDraw =
    draws.find((d) => d.id === selectedId) ?? draws[0] ?? null;
  const showResult =
    selectedDraw?.status === "recorded" && Boolean(selectedDraw?.result);

  let runDisabledReason = "";
  if (preview && !preview.can_run && !preview.already_drawn && preview.eligible_count === 0) {
    runDisabledReason = "No eligible entrants yet - nobody to draw from.";
  }

  return (
    <>
      <Helmet>
        <title>Winners</title>
      </Helmet>

      <Container maxWidth="lg">
        <Typography variant="h4" mb={1}>
          Winners
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Every prize draw across your competitions. Select a draw to see its winner and how it
          was verified.
        </Typography>

        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="warning">{loadError}</Alert>
        ) : draws.length === 0 ? (
          <Alert severity="info">No prize draws are available yet.</Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <DrawList
                draws={draws}
                selectedId={selectedDraw?.id ?? null}
                onSelect={setSelectedId}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              {selectedDraw &&
                (showResult ? (
                  <DrawResultCard
                    result={selectedDraw.result}
                    competitionName={selectedDraw.competitionName}
                    seasonLabel={selectedDraw.seasonLabel}
                    periodLabel={selectedDraw.periodLabel}
                  />
                ) : (
                  <DrawRunPanel
                    draw={selectedDraw}
                    running={running}
                    runError={runError}
                    disabledReason={runDisabledReason}
                    onRunClick={() => {
                      setRunError("");
                      setConfirmOpen(true);
                    }}
                  />
                ))}
            </Grid>
          </Grid>
        )}
      </Container>

      {/* Run confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => !running && setConfirmOpen(false)}>
        <DialogTitle>Run the one-time draw?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This runs the one-time Gameweek 1 prize draw for{" "}
            <strong>
              {[selectedDraw?.competitionName, selectedDraw?.seasonLabel]
                .filter(Boolean)
                .join(" - ") || "the active tournament"}
            </strong>{" "}
            and cannot be undone. One winner will be picked at random from{" "}
            {selectedDraw?.preview?.eligible_count ?? 0} eligible entrant
            {selectedDraw?.preview?.eligible_count === 1 ? "" : "s"}.
          </DialogContentText>
          {runError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {runError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={running}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmRun} disabled={running}>
            {running ? "Running…" : "Run draw"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
